/**
 * Playground Users API Client
 * Uses agent-server /api/v1/users?source=playground directly.
 * No longer depends on admin-server playground_users table.
 */

import { getAgentHeaders, settingsApi } from './settings';
import { usersApi, type TierInfo, type UsageInfo } from './users';

const AGENT_API_BASE_URL = '/api/v1';

// A real user on the allowlist carries its own settings (no source/admin_user_ids) —
// the list marks it with `is_playground: false` and hides the settings-writing controls.
export interface PlaygroundUserSettings {
  source?: 'playground';
  admin_user_ids?: string[];
  is_pinned?: boolean;
  [key: string]: unknown;
}

export interface PlaygroundUser {
  user_id: string;
  tier: TierInfo;
  user: Record<string, unknown>;
  usage: UsageInfo;
  settings: PlaygroundUserSettings;
  is_playground: boolean;
  created_at: string;
  updated_at: string;
}

export interface PlaygroundUserListResponse {
  users: PlaygroundUser[];
  total: number;
}

/**
 * Real (app-signed-up) users the server allowlists for the playground. Fetched one by one
 * from agent-server; an id that no longer resolves is skipped rather than failing the list.
 */
async function fetchRealUsers(): Promise<PlaygroundUser[]> {
  let ids: string[] = [];
  try {
    ids = (await settingsApi.getSystemConfig()).playground?.real_user_ids ?? [];
  } catch (err) {
    console.warn('Failed to load playground real-user allowlist:', err);
    return [];
  }
  const results = await Promise.allSettled(ids.map((id) => usersApi.getById(id)));
  return results.flatMap((r, i) => {
    if (r.status === 'rejected') {
      console.warn(`Playground real user ${ids[i]} unavailable:`, r.reason);
      return [];
    }
    const u = r.value;
    return [
      {
        user_id: u.user_id,
        tier: u.tier,
        user: u.user as Record<string, unknown>,
        usage: u.usage,
        settings: u.settings as PlaygroundUserSettings,
        is_playground: false,
        created_at: u.created_at,
        updated_at: u.updated_at,
      },
    ];
  });
}

export const playgroundUsersApi = {
  /**
   * List playground users visible to the given admin.
   */
  async list(adminUserId: string): Promise<PlaygroundUserListResponse> {
    const params = new URLSearchParams({
      source: 'playground',
      admin_user_id: adminUserId,
      limit: '100',
      order_by: 'updated_at',
      order: 'desc',
    });
    const url = `${AGENT_API_BASE_URL}/users?${params.toString()}`;
    const response = await fetch(url, {
      headers: await getAgentHeaders(),
    });

    if (!response.ok) {
      throw new Error(`Failed to list playground users: ${response.statusText}`);
    }

    const data = await response.json();
    // Sort: pinned first, then by updated_at desc
    const users = (data.users as PlaygroundUser[]).sort((a, b) => {
      const aPinned = a.settings?.is_pinned ? 1 : 0;
      const bPinned = b.settings?.is_pinned ? 1 : 0;
      if (aPinned !== bPinned) return bPinned - aPinned;
      return new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime();
    });

    // Allowlisted real users go on top: chatting as them is the rarer, deliberate case.
    const realUsers = (await fetchRealUsers()).filter((r) => !users.some((u) => u.user_id === r.user_id));
    return { users: [...realUsers, ...users], total: (data.pagination?.total ?? users.length) + realUsers.length };
  },

  /**
   * Toggle pin status via agent-server PATCH /users/:id (settings update).
   */
  async togglePin(userId: string, currentSettings: PlaygroundUserSettings): Promise<void> {
    const response = await fetch(`${AGENT_API_BASE_URL}/users/${userId}`, {
      method: 'PATCH',
      headers: await getAgentHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify({
        settings: {
          ...currentSettings,
          is_pinned: !currentSettings.is_pinned,
        },
      }),
    });

    if (!response.ok) {
      throw new Error(`Failed to toggle pin: ${response.statusText}`);
    }
  },

  /**
   * Update sharing (admin_user_ids) via agent-server PATCH /users/:id.
   */
  async updateSharing(userId: string, currentSettings: PlaygroundUserSettings, adminIds: string[]): Promise<void> {
    const response = await fetch(`${AGENT_API_BASE_URL}/users/${userId}`, {
      method: 'PATCH',
      headers: await getAgentHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify({
        settings: {
          ...currentSettings,
          admin_user_ids: adminIds,
        },
      }),
    });

    if (!response.ok) {
      throw new Error(`Failed to update sharing: ${response.statusText}`);
    }
  },
};

export default playgroundUsersApi;
